import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api, unwrap } from "@/api/client";
import { keys } from "@/api/queries";
import { Banner } from "@/design/status";
import { Card, CardBody, CardHeader, PageHeader } from "@/design/surface";
import { PasswordChangeForm } from "@/features/auth/PasswordChangeForm";

export const Route = createFileRoute("/_app/account/password")({
  beforeLoad: ({ context }) => {
    if (context.info.edition !== "cloud") throw redirect({ to: "/admin" });
  },
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const { t } = useTranslation();
  const { me } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const forced = Boolean(me?.must_change_password);

  async function change(current: string, next: string) {
    await unwrap(api.POST("/api/v1/auth/password", { body: { current_password: current, new_password: next } }));
    await queryClient.invalidateQueries({ queryKey: keys.me });
    toast.success(t("auth.passwordChanged"));
    if (forced) await navigate({ to: "/" });
  }

  return (
    <div className="max-w-xl">
      <PageHeader title={t("auth.changePasswordTitle")} />
      {forced && (
        <Banner tone="info" icon={<KeyRound />} className="mb-4">
          {t("auth.changePasswordForced")}
        </Banner>
      )}
      <Card>
        <CardHeader title={t("shell.changePassword")} />
        <CardBody>
          <PasswordChangeForm onSubmit={change} submitLabel={t("shell.changePassword")} />
        </CardBody>
      </Card>
    </div>
  );
}
